import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Briefcase, GraduationCap, Award, Wrench, Loader2 } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useCareerHistory } from '@/hooks/useCareerHistory';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { reportError } from '@/lib/error-reporting';

interface ParsedData {
  skills: string[];
  work_history: {
    company: string;
    title: string;
    start_date: string | null;
    end_date: string | null;
    description: string | null;
    skills_used: string[];
    is_current: boolean;
  }[];
  education: {
    institution: string;
    degree: string | null;
    field_of_study: string | null;
    start_date: string | null;
    end_date: string | null;
    description: string | null;
  }[];
  certifications: {
    name: string;
    issuing_organization: string | null;
    issue_date: string | null;
    expiration_date: string | null;
    credential_id: string | null;
  }[];
}

interface ResumePreviewConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ParsedData;
  onComplete: () => void;
}

export const ResumePreviewConfirm = ({
  open,
  onOpenChange,
  data,
  onComplete,
}: ResumePreviewConfirmProps) => {
  const { user } = useAuth();
  const { profile, invalidate: invalidateProfile } = useProfile();
  const { saveWorkHistory, saveEducation, saveCertifications } = useCareerHistory();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Track selected items
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(
    () => new Set(data.skills)
  );
  const [selectedWork, setSelectedWork] = useState<Set<number>>(
    () => new Set(data.work_history.map((_, i) => i))
  );
  const [selectedEdu, setSelectedEdu] = useState<Set<number>>(
    () => new Set(data.education.map((_, i) => i))
  );
  const [selectedCerts, setSelectedCerts] = useState<Set<number>>(
    () => new Set(data.certifications.map((_, i) => i))
  );

  // Merge vs replace for skills
  const [skillMode, setSkillMode] = useState<'merge' | 'replace'>('merge');

  const toggleSkill = (skill: string) => {
    setSelectedSkills(prev => {
      const next = new Set(prev);
      if (next.has(skill)) next.delete(skill);
      else next.add(skill);
      return next;
    });
  };

  const toggleSet = (set: Set<number>, index: number, setter: (s: Set<number>) => void) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    try {
      // Save skills to profile
      const newSkills = Array.from(selectedSkills);
      if (newSkills.length > 0) {
        const existingSkills = profile?.skills || [];
        const mergedSkills = skillMode === 'merge'
          ? [...new Set([...existingSkills, ...newSkills])]
          : newSkills;

        await supabase
          .from('profiles')
          .update({ skills: mergedSkills })
          .eq('user_id', user.id);

        invalidateProfile();
      }

      // Save work history
      const workEntries = data.work_history
        .filter((_, i) => selectedWork.has(i))
        .map(w => ({
          company: w.company,
          title: w.title,
          start_date: w.start_date,
          end_date: w.end_date,
          description: w.description,
          skills_used: w.skills_used || [],
          is_current: w.is_current,
        }));

      if (workEntries.length > 0) {
        await saveWorkHistory.mutateAsync(workEntries);
      }

      // Save education
      const eduEntries = data.education
        .filter((_, i) => selectedEdu.has(i))
        .map(e => ({
          institution: e.institution,
          degree: e.degree,
          field_of_study: e.field_of_study,
          start_date: e.start_date,
          end_date: e.end_date,
          description: e.description,
        }));

      if (eduEntries.length > 0) {
        await saveEducation.mutateAsync(eduEntries);
      }

      // Save certifications
      const certEntries = data.certifications
        .filter((_, i) => selectedCerts.has(i))
        .map(c => ({
          name: c.name,
          issuing_organization: c.issuing_organization,
          issue_date: c.issue_date,
          expiration_date: c.expiration_date,
          credential_id: c.credential_id,
        }));

      if (certEntries.length > 0) {
        await saveCertifications.mutateAsync(certEntries);
      }

      toast({
        title: 'Resume imported!',
        description: `Saved ${workEntries.length} positions, ${eduEntries.length} education entries, ${certEntries.length} certifications, and ${newSkills.length} skills.`,
      });

      onComplete();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save resume data';
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
      reportError(err, 'settings', { action: 'resume-save' });
    } finally {
      setSaving(false);
    }
  };

  const totalSelected =
    selectedSkills.size +
    selectedWork.size +
    selectedEdu.size +
    selectedCerts.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Extracted Data</DialogTitle>
          <DialogDescription>
            Select the items you want to import. Uncheck anything that looks incorrect.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Skills */}
          {data.skills.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Skills ({selectedSkills.size}/{data.skills.length})
                </h4>
                <div className="flex gap-1">
                  <Button
                    variant={skillMode === 'merge' ? 'default' : 'outline'}
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setSkillMode('merge')}
                  >
                    Merge
                  </Button>
                  <Button
                    variant={skillMode === 'replace' ? 'default' : 'outline'}
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setSkillMode('replace')}
                  >
                    Replace
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.skills.map(skill => (
                  <Badge
                    key={skill}
                    variant={selectedSkills.has(skill) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {/* Work History */}
          {data.work_history.length > 0 && (
            <section>
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4" />
                Work History ({selectedWork.size}/{data.work_history.length})
              </h4>
              <div className="space-y-2">
                {data.work_history.map((w, i) => (
                  <label
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-md border cursor-pointer hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedWork.has(i)}
                      onCheckedChange={() => toggleSet(selectedWork, i, setSelectedWork)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{w.title}</p>
                      <p className="text-xs text-muted-foreground">{w.company}</p>
                      {(w.start_date || w.end_date) && (
                        <p className="text-xs text-muted-foreground">
                          {w.start_date?.slice(0, 7) || '?'} — {w.is_current ? 'Present' : w.end_date?.slice(0, 7) || '?'}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Education */}
          {data.education.length > 0 && (
            <section>
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                <GraduationCap className="w-4 h-4" />
                Education ({selectedEdu.size}/{data.education.length})
              </h4>
              <div className="space-y-2">
                {data.education.map((e, i) => (
                  <label
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-md border cursor-pointer hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedEdu.has(i)}
                      onCheckedChange={() => toggleSet(selectedEdu, i, setSelectedEdu)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {e.degree ? `${e.degree}${e.field_of_study ? ` in ${e.field_of_study}` : ''}` : e.institution}
                      </p>
                      {e.degree && <p className="text-xs text-muted-foreground">{e.institution}</p>}
                    </div>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Certifications */}
          {data.certifications.length > 0 && (
            <section>
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                <Award className="w-4 h-4" />
                Certifications ({selectedCerts.size}/{data.certifications.length})
              </h4>
              <div className="space-y-2">
                {data.certifications.map((c, i) => (
                  <label
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-md border cursor-pointer hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedCerts.has(i)}
                      onCheckedChange={() => toggleSet(selectedCerts, i, setSelectedCerts)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{c.name}</p>
                      {c.issuing_organization && (
                        <p className="text-xs text-muted-foreground">{c.issuing_organization}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </section>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || totalSelected === 0}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              `Import ${totalSelected} items`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
